import { defineStore } from 'pinia';
import { reactive, ref, toRefs, type Ref } from 'vue';
import { DialogVariants, DialogSizes, type DialogConfig, type DialogVariant, type DialogSize } from '@/constants/dialog';
import type { Component } from 'vue';

const DIALOG_CLOSE_DELAY = 300;

interface DialogState {
    title: string;
    message: string;
    size: DialogSize;
    variant: DialogVariant;
    actionText: string;
    actionIcon: Component | null;
    cancelText: string;
    cancelIcon: Component | null;
    hideNoButton: boolean;
    data: any;
}

interface PromiseResolvers {
    resolve: (value: boolean) => void;
    reject: (reason?: any) => void;
}

const DEFAULT_STATE: DialogState = {
    title: '',
    message: '',
    size: DialogSizes.MD,
    variant: DialogVariants.Danger,
    actionText: 'Oke',
    actionIcon: null,
    cancelText: 'Annuleren',
    cancelIcon: null,
    hideNoButton: false,
    data: null,
};

export const useDialogStore = defineStore('dialog', () => {
    const active: Ref<boolean> = ref(false);
    const loading: Ref<boolean> = ref(false);
    const resolve: Ref<PromiseResolvers | null> = ref(null);
    
    const state = reactive<DialogState>({ ...DEFAULT_STATE });

    const resetState = (): void => {
        Object.assign(state, DEFAULT_STATE);
        loading.value = false;
        resolve.value = null;
    };

    const open = (options: DialogConfig): Promise<boolean> => {
        if (active.value) {
            console.warn('Dialog is already open');
            return Promise.resolve(false);
        }

        if (!options.title || !options.message) {
            console.error('Dialog requires both title and message');
            return Promise.reject(new Error('Invalid dialog configuration'));
        }

        active.value = true;
        
        Object.assign(state, {
            title: options.title,
            message: options.message,
            size: options.size ?? DEFAULT_STATE.size,
            variant: options.variant ?? DEFAULT_STATE.variant,
            actionText: options.actionText ?? DEFAULT_STATE.actionText,
            actionIcon: options.actionIcon ?? DEFAULT_STATE.actionIcon,
            cancelText: options.cancelText ?? DEFAULT_STATE.cancelText,
            cancelIcon: options.cancelIcon ?? DEFAULT_STATE.cancelIcon,
            hideNoButton: options.hideNoButton ?? DEFAULT_STATE.hideNoButton,
            data: options.data ?? DEFAULT_STATE.data,
        });

        return new Promise<boolean>((res, rej) => {
            resolve.value = { resolve: res, reject: rej };
        });
    };

    const close = (): void => {
        if (resolve.value) {
            resolve.value.resolve(false);
        }
        
        active.value = false;
        setTimeout(resetState, DIALOG_CLOSE_DELAY);
    };

    const confirm = (result: boolean = true): void => {
        if (resolve.value) {
            resolve.value.resolve(result);
        }
        
        active.value = false;
        setTimeout(resetState, DIALOG_CLOSE_DELAY);
    };

    const setLoading = (isLoading: boolean): void => {
        loading.value = isLoading;
    };

    return {
        active,
        loading,
        resolve,
        ...toRefs(state),
        open,
        close,
        confirm,
        setLoading,
        resetState,
    };
});
